import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Poll, PollOption, PollVote, PollWithDetails, PollOptionWithVotes } from '../types';

/**
 * Hook to manage a single poll with real-time updates
 */
export const usePoll = (pollId: string | undefined, currentUserId: string | undefined) => {
  const [poll, setPoll] = useState<PollWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voting, setVoting] = useState(false);

  const fetchPoll = useCallback(async () => {
    if (!pollId || !currentUserId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch poll with options and votes
      const { data: pollData, error: pollError } = await supabase
        .from('polls')
        .select(`
          *,
          creator:profiles!polls_created_by_fkey(id, full_name, nickname, avatar_url),
          options:poll_options(
            *,
            votes:poll_votes(*)
          )
        `)
        .eq('id', pollId)
        .single();

      if (pollError) throw pollError;

      if (pollData) {
        // Transform data to include vote counts and user voting status
        const options: PollOptionWithVotes[] = (pollData.options || [])
          .sort((a: PollOption, b: PollOption) => a.option_order - b.option_order)
          .map((option: PollOption & { votes: PollVote[] }) => ({
            ...option,
            vote_count: option.votes?.length || 0,
            voted_by_current_user: option.votes?.some(
              (v: PollVote) => v.user_id === currentUserId
            ) || false,
          }));

        const totalVotes = options.reduce((sum, opt) => sum + opt.vote_count, 0);
        const hasVoted = options.some((opt) => opt.voted_by_current_user);

        setPoll({
          ...pollData,
          options,
          total_votes: totalVotes,
          has_voted: hasVoted,
          creator: pollData.creator,
        });
      }
    } catch (err) {
      console.error('Error fetching poll:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch poll');
    } finally {
      setLoading(false);
    }
  }, [pollId, currentUserId]);

  useEffect(() => {
    fetchPoll();

    // Real-time subscription for votes
    if (pollId) {
      const channel = supabase
        .channel(`poll-${pollId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'poll_votes', filter: `poll_id=eq.${pollId}` },
          () => fetchPoll()
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'polls', filter: `id=eq.${pollId}` },
          () => fetchPoll()
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [fetchPoll, pollId]);

  /**
   * Vote for an option
   */
  const vote = useCallback(
    async (optionId: string) => {
      if (!poll || !currentUserId || voting) return false;

      // Check if poll is closed
      if (poll.is_closed) {
        setError('Deze poll is gesloten');
        return false;
      }

      // Check if already voted and multiple answers not allowed
      if (poll.has_voted && !poll.allows_multiple_answers) {
        setError('Je hebt al gestemd op deze poll');
        return false;
      }

      // Check if already voted for this option
      const option = poll.options.find((o) => o.id === optionId);
      if (option?.voted_by_current_user) {
        // Remove vote instead
        return removeVote(optionId);
      }

      try {
        setVoting(true);
        setError(null);

        const { error: voteError } = await supabase.from('poll_votes').insert({
          poll_id: poll.id,
          option_id: optionId,
          user_id: currentUserId,
        });

        if (voteError) throw voteError;

        await fetchPoll();
        return true;
      } catch (err) {
        console.error('Error voting:', err);
        setError(err instanceof Error ? err.message : 'Kon niet stemmen');
        return false;
      } finally {
        setVoting(false);
      }
    },
    [poll, currentUserId, voting, fetchPoll]
  );

  /**
   * Remove a vote
   */
  const removeVote = useCallback(
    async (optionId: string) => {
      if (!poll || !currentUserId || voting) return false;

      try {
        setVoting(true);
        setError(null);

        const { error: deleteError } = await supabase
          .from('poll_votes')
          .delete()
          .eq('poll_id', poll.id)
          .eq('option_id', optionId)
          .eq('user_id', currentUserId);

        if (deleteError) throw deleteError;

        await fetchPoll();
        return true;
      } catch (err) {
        console.error('Error removing vote:', err);
        setError(err instanceof Error ? err.message : 'Kon stem niet verwijderen');
        return false;
      } finally {
        setVoting(false);
      }
    },
    [poll, currentUserId, voting, fetchPoll]
  );

  /**
   * Close the poll (only creator can do this)
   */
  const closePoll = useCallback(async () => {
    if (!poll || poll.created_by !== currentUserId) return false;

    try {
      const { error: updateError } = await supabase
        .from('polls')
        .update({ is_closed: true, updated_at: new Date().toISOString() })
        .eq('id', poll.id);

      if (updateError) throw updateError;

      await fetchPoll();
      return true;
    } catch (err) {
      console.error('Error closing poll:', err);
      setError(err instanceof Error ? err.message : 'Kon poll niet sluiten');
      return false;
    }
  }, [poll, currentUserId, fetchPoll]);

  return {
    poll,
    loading,
    error,
    voting,
    vote,
    removeVote,
    closePoll,
    refetch: fetchPoll,
  };
};

